/**
 * WeightBearingSkillEffect — Earth tree wall fork 2 terminal passive (fortify_passive).
 *
 * While the player has been stationary for at least 2000ms, every active
 * construction gains 20% HP. The bonus is applied as a delta to each terrain
 * object's current HP, capped at `config.hp × 1.2`. On movement (when the
 * stationary threshold is lost), the bonus is removed — each terrain's HP is
 * clamped back to `config.hp`, preserving any damage taken during the bonus
 * window but not carrying the bonus HP beyond the base max.
 *
 * New terrain placed while the 2000ms threshold is active receives the bonus
 * on the next update tick (within one 16ms frame).
 *
 * The 2000ms threshold is unique among Earth stationary passives:
 * - Bedrock, TremorSense, Iron Seam, Deep Root: 1000ms
 * - Weight-Bearing: 2000ms (this node)
 * All share the same StationaryTracker instance — one counter, multiple thresholds.
 *
 * UpgradeEffectResolver key(s):
 * - `constructionHpBonus`: value 0.2, aggregation additive, scope tree
 * - `stationaryRequirementMs`: value 2000 (metadata only, not consumed via resolver)
 *
 * Implementation notes:
 * - Uses the delta-on-enter/exit pattern: apply (+delta) when crossing the
 *   threshold, remove (clamp to config.hp) when losing it.
 * - Tracks which terrain IDs have received the bonus in `_bonusApplied` to
 *   avoid double-application. Dead terrain is never in `getAll()`, so the set
 *   accumulates expired IDs harmlessly until the next removal sweep.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Weight-Bearing (fortify_passive)
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { ActiveTerrain } from '../../../systems/TerrainConstructionSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface WeightBearingDeps {
  readonly stationary: {
    /**
     * Returns true when the player has been continuously stationary for at
     * least `thresholdMs` milliseconds. Shared StationaryTracker instance.
     */
    isStationary(thresholdMs: number): boolean;
  };
  readonly terrain: {
    /**
     * Returns all living (non-expired, non-destroyed) terrain instances.
     * Their `hp` property is directly mutable — WeightBearing writes to it.
     */
    getAll(): readonly ActiveTerrain[];
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface WeightBearingConfig {
  /** Stationary duration (ms) before the HP bonus activates. */
  readonly stationaryRequirementMs: number;
  /** Fractional HP bonus applied to each active terrain (0.2 = 20%). */
  readonly hpBonusMultiplier: number;
}

const DEFAULT_CONFIG: WeightBearingConfig = {
  stationaryRequirementMs: 2000, // unique 2-second threshold
  hpBonusMultiplier: 0.2,        // 20% HP bonus
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class WeightBearingSkillEffect implements SkillEffect {
  readonly skillId = 'fortify_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: WeightBearingDeps;
  private readonly config: WeightBearingConfig;

  /**
   * Whether the 2000ms stationary threshold was active at the end of the
   * previous update tick. Drives enter/exit transition detection.
   */
  private _wasStationary = false;

  /**
   * IDs of terrain objects that currently have the Weight-Bearing HP bonus
   * applied. Used to avoid double-adding the delta on successive frames.
   * Dead terrain IDs remain in the set until the next removal sweep — they
   * are harmless since `getAll()` never returns dead terrain.
   */
  private _bonusApplied = new Set<string>();

  constructor(deps: WeightBearingDeps, config: Partial<WeightBearingConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Passive query interface ---

  /** Whether the HP bonus is currently active (2000ms threshold met). */
  get isWeightBearingActive(): boolean {
    return this.deps.stationary.isStationary(this.config.stationaryRequirementMs);
  }

  get stationaryRequirementMs(): number { return this.config.stationaryRequirementMs; }
  get hpBonusMultiplier(): number { return this.config.hpBonusMultiplier; }

  // --- SkillEffect interface ---

  /**
   * Per-frame update. Detects stationary threshold transitions and applies
   * or removes the HP bonus from active terrain objects accordingly.
   *
   * On enter (not-stationary → stationary): all active terrain gains the bonus.
   * On exit (stationary → not-stationary): all active terrain is clamped back.
   * While stationary: any newly placed terrain (not yet in `_bonusApplied`)
   * receives the bonus on this tick.
   */
  update(_delta: number): void {
    const currentlyStationary = this.deps.stationary.isStationary(
      this.config.stationaryRequirementMs,
    );

    if (!this._wasStationary && currentlyStationary) {
      // Threshold just crossed — apply bonus to all existing terrain
      this._applyBonusToUncovered();
    } else if (this._wasStationary && !currentlyStationary) {
      // Threshold lost — remove bonus from all terrain
      this._removeBonusFromAll();
    } else if (currentlyStationary) {
      // Still stationary — handle any terrain placed since the last tick
      this._applyBonusToUncovered();
    }

    this._wasStationary = currentlyStationary;
  }

  destroy(): void {
    this._bonusApplied.clear();
    this._wasStationary = false;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Applies the HP bonus to any active terrain not yet in `_bonusApplied`.
   * The delta is `config.hp × hpBonusMultiplier`, capped at `config.hp × (1 + multiplier)`.
   * Called on threshold-enter and on every stationary frame (handles new terrain).
   */
  private _applyBonusToUncovered(): void {
    for (const terrain of this.deps.terrain.getAll()) {
      if (this._bonusApplied.has(terrain.terrainId)) continue;
      const bonus = terrain.config.hp * this.config.hpBonusMultiplier;
      const maxWithBonus = terrain.config.hp * (1 + this.config.hpBonusMultiplier);
      terrain.hp = Math.min(terrain.hp + bonus, maxWithBonus);
      this._bonusApplied.add(terrain.terrainId);
    }
  }

  /**
   * Removes the HP bonus from all currently active terrain by clamping each
   * object's HP back to its base `config.hp`. Damage taken while the bonus
   * was active is preserved (clamped only if current HP exceeds the base max).
   * Clears `_bonusApplied` after processing.
   */
  private _removeBonusFromAll(): void {
    for (const terrain of this.deps.terrain.getAll()) {
      terrain.hp = Math.min(terrain.hp, terrain.config.hp);
    }
    this._bonusApplied.clear();
  }
}
